import React, { useState } from "react";
import { Checkbox, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { SubtaskItemType } from "../types";
import { InputField } from "./InputField";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import { SubtaskRenderer } from "./index";
import type { SubtaskItem } from "../types";

const getStringConfig = (config: Record<string, unknown>, key: string): string | undefined => {
  const val = config[key];
  return typeof val === "string" ? val : undefined;
};

interface ChecklistItemProps {
  item: SubtaskItem;
  disabled?: boolean;
}

export const ChecklistItem: React.FC<ChecklistItemProps> = ({ item, disabled = false }) => {
  const derivedChecked = !!item.isCompleted || !!item.value;
  const [checked, setChecked] = useState<boolean>(derivedChecked);
  const [lastSeen, setLastSeen] = useState<{ id: SubtaskItem["id"]; value: boolean }>({
    id: item.id,
    value: derivedChecked,
  });
  if (lastSeen.id !== item.id || lastSeen.value !== derivedChecked) {
    setLastSeen({ id: item.id, value: derivedChecked });
    setChecked(derivedChecked);
  }
  const { toggleSubtask } = useSubtaskActionsContext();

  const handleChange = () => {
    if (disabled) return;
    const next = !checked;
    setChecked(next);
    void toggleSubtask(item.id, next);
  };

  // Build label — attestation gets 🔒 prefix
  const label = item.itemType === SubtaskItemType.ATTESTATION ? `🔒 ${item.label}` : item.label;

  return (
    <div className={styles.checklistItemWrapper}>
      <div className={styles.checklistRow}>
        <Checkbox
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          label={label}
          data-testid={`checklist-${item.id}`}
        />
      </div>

      {checked && item.helpText && (
        <Flex alignItems="center" gap={Sizes.XS} className={styles.helpTextWarning}>
          <span className={styles.helpTextWarningIcon} aria-hidden="true">
            <Icon type="exclamation-triangle" variant="solid" size="small" />
          </span>
          <Text variant="secondary-sm" color={Colors.Warning600}>
            {item.helpText}
          </Text>
        </Flex>
      )}

      {/* Composite: CHECKBOX_WITH_INPUT — show input when checked */}
      {checked && item.itemType === SubtaskItemType.CHECKBOX_WITH_INPUT && (
        <div className={styles.childrenContainer}>
          <InputField
            item={{
              ...item,
              id: item.id,
              itemType: SubtaskItemType.INPUT_FIELD,
              label: getStringConfig(item.config ?? {}, "inputLabel") ?? item.label,
              config: item.config,
              // Live: the input is only rendered while the box is checked.
              // Without this override, persisting a value would race against
              // a stale prop and un-tick the parent box on the server.
              isCompleted: true,
            }}
            disabled={disabled}
          />
        </div>
      )}

      {/* Conditional children — rendered when checked. Children arrive from the
          backend conditionalChildren JSON blob; a materialized child row with a
          null item_type/data_type surfaces here with itemType === undefined,
          which would fall through SubtaskRenderer's default and render nothing,
          leaving an empty container. Coerce any unrecognized itemType to
          DOCUMENT_UPLOAD so the file-upload control always surfaces. */}
      {checked && item.conditional_children && (
        <div className={styles.childrenContainer}>
          {Object.values(item.conditional_children).map((children) =>
            children.map((child) => {
              const isKnownType = Object.values(SubtaskItemType).includes(child.itemType);
              const normalizedChild = isKnownType
                ? child
                : { ...child, itemType: SubtaskItemType.DOCUMENT_UPLOAD };
              return (
                <SubtaskRenderer key={child.id} item={normalizedChild} disabled={disabled} />
              );
            }),
          )}
        </div>
      )}
    </div>
  );
};

export default ChecklistItem;
